import { memo, useState } from 'react';
import dayjs from 'dayjs';

import { Button, Card, Empty, Space, Tag, Typography } from 'antd';

import {
  CandidateOfferEvent,
  CandidateOfferEventType,
} from '../../../../../fsm/jobs';
import RecordOfferEventModal, {
  RecordOfferEventPayload,
} from '../record-offer-event-modal';

import styles from './index.module.css';

const { Text, Title } = Typography;

interface Props {
  isLoading: boolean;
  offerEvents: CandidateOfferEvent[];
  candidateName?: string;
  confirmingUserName: string;
  confirmingUserRole: string;
  /**
   * Whether this user may record (not just read) an offer event. Narrower
   * than the panel's own visibility - HIRING_MANAGER can still see the
   * history below, but can no longer record, since recording now bills the
   * customer (Nachi ruled 2026-10-01; see CandidateOfferEventService's
   * narrower @PreAuthorize on recordOfferEvent vs. getOfferEvents).
   */
  canRecordOfferEvents: boolean;
  onRecordOfferEvent: (
    eventType: CandidateOfferEventType,
    payload: RecordOfferEventPayload,
  ) => void;
}

const eventTypeLabel = (eventType: CandidateOfferEventType) =>
  eventType === CandidateOfferEventType.OFFER_ACCEPTED
    ? 'Offer accepted'
    : 'Offer made';

const eventTypeColor = (eventType: CandidateOfferEventType) =>
  eventType === CandidateOfferEventType.OFFER_ACCEPTED ? 'green' : 'blue';

/**
 * Offer tracking - the billing trigger for priority #3. Every event here is
 * permanent once recorded (RecordOfferEventModal is the only write path, and
 * it requires a deliberate confirmation - see its own docstring for the
 * persona-testing finding this whole feature exists to fix).
 */
const OfferEventsPanel = ({
  isLoading,
  offerEvents,
  candidateName,
  confirmingUserName,
  confirmingUserRole,
  canRecordOfferEvents,
  onRecordOfferEvent,
}: Props) => {
  const [openFor, setOpenFor] = useState<CandidateOfferEventType | null>(null);

  const handleSubmit = (payload: RecordOfferEventPayload) => {
    if (openFor) {
      onRecordOfferEvent(openFor, payload);
    }
  };

  // Client-side mirror of the backend's rejection of a second OFFER_MADE for
  // the same candidate+job (CandidateOfferEventRepository#existsByCandidate_
  // IdAndPosition_IdAndEventType). The backend remains the source of truth -
  // this only prevents the obvious re-click; it does not replace that check.
  const hasOfferMade = offerEvents.some(
    (event) => event.eventType === CandidateOfferEventType.OFFER_MADE,
  );

  return (
    <Card size="small" style={{ width: '100%' }} title="Offer tracking">
      {canRecordOfferEvents && (
        <Space className={styles.actions}>
          <Button
            disabled={hasOfferMade}
            title={hasOfferMade ? 'An offer has already been made for this candidate' : undefined}
            onClick={() => setOpenFor(CandidateOfferEventType.OFFER_MADE)}
          >
            Mark offer made
          </Button>
          <Button
            onClick={() => setOpenFor(CandidateOfferEventType.OFFER_ACCEPTED)}
          >
            Mark offer accepted
          </Button>
        </Space>
      )}

      {offerEvents.length === 0 ? (
        <Empty
          description="No offer events recorded yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div className={styles.list}>
          {offerEvents.map((event) => (
            <div key={event.id} className={styles.event}>
              <Space align="start" className={styles.eventHead}>
                <Tag color={eventTypeColor(event.eventType)}>
                  {eventTypeLabel(event.eventType)}
                </Tag>
                {event.offerType && <Tag>{event.offerType}</Tag>}
              </Space>
              <Title level={5} className={styles.eventTitle}>
                {event.compensationSummary || 'No compensation summary provided'}
              </Title>
              {event.proposedStartDate && (
                <Text type="secondary" className={styles.line}>
                  Proposed start date:{' '}
                  {dayjs(event.proposedStartDate).format('MMM D, YYYY')}
                </Text>
              )}
              {event.notes && (
                <Text className={styles.line}>{event.notes}</Text>
              )}
              <Text type="secondary" className={styles.provenance}>
                Recorded {dayjs(event.occurredAt).format('MMM D, YYYY h:mm A')}{' '}
                by {event.recordedByName} ({event.recordedByRole}) &mdash;
                timestamp set by Avrio&apos;s server, not this browser.
              </Text>
            </div>
          ))}
        </div>
      )}

      <RecordOfferEventModal
        isOpen={openFor !== null}
        isLoading={isLoading}
        eventType={openFor ?? CandidateOfferEventType.OFFER_MADE}
        candidateName={candidateName}
        confirmingUserName={confirmingUserName}
        confirmingUserRole={confirmingUserRole}
        onClose={() => setOpenFor(null)}
        onSubmit={handleSubmit}
      />
    </Card>
  );
};

export default memo(OfferEventsPanel);
